import { canNavigate } from '@/plugins/acl/routeProtection'
import ability from '@/plugins/acl/ability'
import Vue from 'vue'
import VueRouter from 'vue-router'
import apps from './apps'
import dashboard from './dashboard'
import pages from './pages'
import axios from '@axios'
import store from '@/store'
import { msalInstance } from '@/plugins/auth/msal-plugin'
import { foundryRequest } from '@/config/authConfig'


Vue.use(VueRouter)

const routes = [
  // ? We are redirecting to different pages based on role.
  // NOTE: Role is just for UI purposes. ACL is based on abilities.
  {
    path: '/',
    redirect: to => {
      const userData = JSON.parse(localStorage.getItem('user') || '{}')
      const userRole = userData && userData.role ? userData.role.name : null
      const isNotUser = userData && Object.keys(userData).length === 0 && Object.getPrototypeOf(userData) === Object.prototype

      if (['Director'].includes(userRole)) return  { name: 'dashboard-sales' }
      else if (isNotUser) return { name: 'auth-login', query: to.query }
      else  return  { name: 'project-list' }
      // if (userRole) return { name: 'my-board', params: {id: userData.email} }
      
    },
  },
  {
    path: '/error-404',
    name: 'error-404',
    component: () => import('@/views/Error404.vue'),
    meta: {
      title: '404',
      layout: 'blank',
      resource: 'public',
    },
  },
  {
    path: '/login',
    name: 'auth-login',
    component: () => import('@/views/Login.vue'),
    meta: {
      title: 'Login',
      layout: 'blank',
      resource: 'public',
      redirectIfLoggedIn: true,
    },
  },
  {
    path: '/logout',
    name: 'auth-logout',
    meta: {
      layout: 'blank',
      resource: 'public'
    },
    beforeEnter: (to, from, next) => {
      // 1. Clear LocalStorage
      localStorage.removeItem('accessToken')
      localStorage.removeItem('userData')
      localStorage.removeItem('user')
      localStorage.removeItem('userAbility')
      localStorage.removeItem('timeline-filter')
      localStorage.removeItem('project-filter')
      
      msalInstance.logoutRedirect({
          postLogoutRedirectUri: window.location.origin + '/login'
      });
    },
  },
  {
    path: '/register',
    name: 'auth-register',
    component: () => import('@/views/Register.vue'),
    meta: {
      layout: 'blank',
      resource: 'public',
      redirectIfLoggedIn: true,
    },
  },
  {
    path: '/forgot-password',
    name: 'auth-forgot-password',
    component: () => import('@/views/ForgotPassword.vue'),
    meta: {
      layout: 'blank',
      resource: 'public',
      redirectIfLoggedIn: true,
    },
  },
  ...dashboard,
  ...apps,
  ...pages,
  {
    path: '*',
    redirect: 'error-404',
  },
]

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  routes,
  scrollBehavior() {
    return { x: 0, y: 0 }
  },
})


router.beforeEach(async (to, from, next) => {
  // 1. Wait for any pending MSAL Microsoft redirects to finish before checking accounts
  try {
    await msalInstance.handleRedirectPromise();
  } catch (error) {
    // It is safe to ignore the hash_empty_error (it just means MSAL's cache is clearing)
    if (error.name !== 'BrowserAuthError' || !error.message.includes('hash_empty')) {
      console.error('MSAL Redirect Error:', error);
    }
  }

  // 2. Check Auth Status
  const accounts = msalInstance.getAllAccounts();
  if (accounts.length > 0 && !msalInstance.getActiveAccount()) {
    msalInstance.setActiveAccount(accounts[0]);
  }
  const account = msalInstance.getActiveAccount();
  const isAuthenticated = !!account; // True if an account exists

  const isAuthRoute = to.name === 'auth-login';
  const isPublicRoute = to.meta.public || isAuthRoute;


  // ==========================================
  // UNAUTHENTICATED FLOW
  // ==========================================
  if (!isAuthenticated) {
    // If they are already going to a public route or login page, let them through!
    if (isPublicRoute) {
      return next(); 
    }
    if (to.query.code || to.query.state || window.location.search.includes('code=')) {
      return next();
    }

    // Otherwise, redirect to login page (this won't loop because of the check above)
    return next({ name: 'auth-login', query: { marketplace: to.query.marketplace } });
  }

  // ==========================================
  // AUTHENTICATED FLOW
  // ==========================================
  
  // If they are logged in but trying to go to the login page, redirect to home
  let userAbility = JSON.parse(localStorage.getItem('userAbility') || 'null');
  let userData = JSON.parse(localStorage.getItem('user') || 'null');

  if (!userData || !userAbility) {
    try {
      // Fetch profile using Axios (which automatically gets the MSAL token via the interceptor!)
      const tokenResponse = await msalInstance.acquireTokenSilent({
        scopes: foundryRequest.scopes,
        account: account
      });
      
      localStorage.setItem('accessToken', `Bearer ${tokenResponse.accessToken}`);

      const userResponse = await axios.get('/users/profiles/me/');
      userData = userResponse.data;
      userAbility = userData.user_ability;
      
      delete userData.user_ability;
      
      // Save to local storage
      localStorage.setItem('userAbility', JSON.stringify(userAbility));
      localStorage.setItem('user', JSON.stringify(userData));
      
      // MUST update ability instance so canNavigate() works properly on this route
      if (typeof ability !== 'undefined') {
        ability.update(userAbility); 
      }
      
    } catch (error) {
      console.error("Failed to fetch user profile/abilities. Logging out.", error);
      localStorage.clear();
      msalInstance.logoutRedirect({ postLogoutRedirectUri: window.location.origin + '/login' });
      return; // Stop routing
    }
  }

  // If logged in but trying to access the login page, redirect to home
  if (to.meta.redirectIfLoggedIn || isAuthRoute) {
    return next('/');
  }

  // ACL Check (Are they allowed on this page?)
  if (!canNavigate(to)) {
    return next({ name: 'misc-not-authorized' });
  }

  // ==========================================
  // SET PAGE TITLE & PROCEED
  // ==========================================
  let title = 'LH - ' + (to.meta.title || 'PRS IN VIVO Projects');

  if (to.params){
    const keys = Object.keys(to.params);
    if (keys.length) {
      title = `LH - ${to.meta.title}: ${to.params[keys[0]]}`;
      if (to.params[keys[1]]) {
        title += ` ${to.params[keys[1]]}`;
      }
    }
  }
  document.title = title;

  return next();
});
export default router
